import { useState, useEffect } from "react";
import {
  ref,
  onValue,
  push,
  remove,
  update,
  set,
  get,
} from "firebase/database";
import { getAuth } from "firebase/auth";
import { db as database } from "../Firebase/firestore";
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { Pencil, Trash2, X, Send } from "lucide-react";
import "./broadcast.css";

const BroadcastingPage = () => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [activeTab, setActiveTab] = useState("compose");
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });
  const [searchTerm, setSearchTerm] = useState("");

  // Image handling
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImageUrls, setExistingImageUrls] = useState([]);

  const [validity, setValidity] = useState("2 days");
  const [editingDraftId, setEditingDraftId] = useState(null);

  // Progress
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentUploadingIndex, setCurrentUploadingIndex] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid || "";

  useEffect(() => {
    if (!auth.currentUser)
      showNotification("Please sign in to use broadcasting", "error");
  }, []);

  const showNotification = (message, type = "success") => {
    setNotification({ show: true, message, type });
    setTimeout(
      () => setNotification({ show: false, message: "", type: "" }),
      4000,
    );
  };

  // Fetch Users
  useEffect(() => {
    const profileRef = ref(database, "profileDashboard");
    const unsubscribe = onValue(profileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const userList = Object.keys(data).map((userId) => ({
          userId,
          company_name: data[userId].company_name || "Unknown Company",
        }));
        setUsers(
          userList.sort((a, b) => a.company_name.localeCompare(b.company_name)),
        );
      } else setUsers([]);
    });
    return () => unsubscribe();
  }, []);

  // Fetch Broadcasts
  useEffect(() => {
    if (!currentUserId) return;
    const broadcastRef = ref(database, `broadcasts/${currentUserId}`);
    const unsubscribe = onValue(broadcastRef, (snapshot) => {
      const data = snapshot.val();
      setBroadcasts(
        data ? Object.keys(data).map((id) => ({ id, ...data[id] })) : [],
      );
    });
    return () => unsubscribe();
  }, [currentUserId]);
  // Auto remove expired broadcasts from all users
  useEffect(() => {
    if (!currentUserId || broadcasts.length === 0) return;

    const checkAndRemoveExpired = async () => {
      const now = Date.now();

      for (const b of broadcasts) {
        if (b.status !== true) continue;

        const expiryTime = Number(
          b.expiryTime || new Date(b.timestamp).getTime() + 172800000,
        );

        if (expiryTime <= now) {
          try {
            // Get latest broadcast data directly from DB
            const senderBroadcastRef = ref(
              database,
              `broadcasts/${currentUserId}/${b.id}`,
            );

            const snapshot = await get(senderBroadcastRef);

            if (!snapshot.exists()) continue;

            const latestData = snapshot.val();
            const recipients = latestData?.recipients || [];

            // Remove from sender
            await remove(senderBroadcastRef);

            // Remove from all recipients
            if (Array.isArray(recipients) && recipients.length > 0) {
              await Promise.all(
                recipients.map(async (rid) => {
                  try {
                    await remove(ref(database, `broadcasts/${rid}/${b.id}`));
                  } catch (err) {
                    console.error(
                      `Failed removing recipient broadcast for ${rid}:`,
                      err,
                    );
                  }
                }),
              );
            }

            console.log(`Expired broadcast removed: ${b.id}`);
          } catch (err) {
            console.error("Failed to remove expired broadcast:", err);
          }
        }
      }
    };

    checkAndRemoveExpired();

    const interval = setInterval(checkAndRemoveExpired, 60000);

    return () => clearInterval(interval);
  }, [broadcasts, currentUserId]);

  const handleDelete = async (id, type) => {
    if (!window.confirm(`Delete this ${type}?`)) return;
    try {
      const broadcast = broadcasts.find((b) => b.id === id);
      await remove(ref(database, `broadcasts/${currentUserId}/${id}`));
      if (broadcast?.recipients?.length > 0) {
        await Promise.all(
          broadcast.recipients.map((rid) =>
            remove(ref(database, `broadcasts/${rid}/${id}`)),
          ),
        );
      }
      showNotification(`${type} deleted successfully`);
    } catch (err) {
      showNotification("Delete failed", "error");
    }
  };

  const handleEditDraft = (draft) => {
    setValidity(draft.validity || "2 days");
    setSelectedUsers(draft.recipients || []);
    setEditingDraftId(draft.id);

    if (draft.imageUrl) {
      setExistingImageUrls([draft.imageUrl]);
      setImagePreviews([draft.imageUrl]);
    } else {
      setExistingImageUrls([]);
      setImagePreviews([]);
    }
    setImageFiles([]);
    setActiveTab("compose");
  };

  const handleUserSelect = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files).filter(
      (file) => file.type.startsWith("image/") && file.size <= 5 * 1024 * 1024,
    );

    if (files.length === 0)
      return showNotification("No valid images selected", "error");

    const newPreviews = files.map((file) => URL.createObjectURL(file));
    setImageFiles((prev) => [...prev, ...files]);
    setImagePreviews((prev) => [...prev, ...newPreviews]);
    setExistingImageUrls([]);
  };

  const removeImage = (index) => {
    if (imagePreviews[index]?.startsWith("blob:"))
      URL.revokeObjectURL(imagePreviews[index]);
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
    setExistingImageUrls((prev) => prev.filter((_, i) => i !== index));
  };

  // Drag and Drop Reordering
  const moveImage = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const newFiles = [...imageFiles];
    const newPreviews = [...imagePreviews];

    [newFiles[fromIndex], newFiles[toIndex]] = [
      newFiles[toIndex],
      newFiles[fromIndex],
    ];
    [newPreviews[fromIndex], newPreviews[toIndex]] = [
      newPreviews[toIndex],
      newPreviews[fromIndex],
    ];

    setImageFiles(newFiles);
    setImagePreviews(newPreviews);
  };

  const uploadSingleImage = async (file, broadcastId, index) => {
    const storage = getStorage();
    const fileName = `${Date.now()}-${index}-${file.name}`;
    const imageStorageRef = storageRef(
      storage,
      `broadcasts/${currentUserId}/${broadcastId}/${fileName}`,
    );

    const snapshot = await uploadBytes(imageStorageRef, file);
    return await getDownloadURL(snapshot.ref);
  };

  const saveBroadcast = async (action, draftToSend = null) => {
    const isSendingDraft = !!draftToSend;
    if (!isSendingDraft && imageFiles.length + existingImageUrls.length === 0) {
      return showNotification("Please select at least one image", "error");
    }
    if (selectedUsers.length === 0) {
      return showNotification("Please select at least one recipient", "error");
    }

    setLoading(true);
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const finalImageUrls = isSendingDraft
        ? [draftToSend.imageUrl]
        : [...existingImageUrls];

      if (!isSendingDraft && imageFiles.length > 0) {
        for (let i = 0; i < imageFiles.length; i++) {
          setCurrentUploadingIndex(i + 1);
          const tempId = `img_${Date.now()}`;
          const url = await uploadSingleImage(imageFiles[i], tempId, i);
          finalImageUrls.push(url);
          setUploadProgress(Math.round(((i + 1) / imageFiles.length) * 100));
        }
      }

      for (let i = 0; i < finalImageUrls.length; i++) {
        const imageUrl = finalImageUrls[i];
        const validityMs =
          {
            "1 day": 86400000,
            "2 days": 172800000,
            "1 week": 604800000,
            "2 weeks": 1209600000,
            "1 month": 2592000000,
          }[validity] || 172800000;

        const now = Date.now();

        const data = {
          imageUrl,
          validity,
          senderId: currentUserId,
          timestamp: now,
          expiryTime: now + validityMs,
          recipients: selectedUsers,
        };

        let broadcastId = draftToSend?.id || null;

        if (broadcastId && i === 0) {
          await set(
            ref(database, `broadcasts/${currentUserId}/${broadcastId}`),
            {
              ...data,
              status: true,
            },
          );
        } else {
          const broadcastRef = push(
            ref(database, `broadcasts/${currentUserId}`),
          );
          broadcastId = broadcastRef.key;

          const fullData = { ...data, id: broadcastId, status: true };

          await set(
            ref(database, `broadcasts/${currentUserId}/${broadcastId}`),
            fullData,
          );

          await Promise.all(
            selectedUsers.map((rid) =>
              set(ref(database, `broadcasts/${rid}/${broadcastId}`), fullData),
            ),
          );
        }
      }

      // Reset
      setImageFiles([]);
      setImagePreviews([]);
      setExistingImageUrls([]);
      setSelectedUsers([]);
      setValidity("2 days");
      setEditingDraftId(null);
      setUploadProgress(0);

      showNotification(
        isSendingDraft
          ? "Draft sent successfully!"
          : `${finalImageUrls.length} image(s) sent successfully!`,
      );
      setActiveTab("sent");
    } catch (err) {
      console.error(err);
      showNotification("Operation failed", "error");
    } finally {
      setLoading(false);
      setIsUploading(false);
    }
  };

  const filteredUsers = users.filter((u) =>
    u.company_name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const sentBroadcasts = broadcasts.filter((b) => b.status === true);
  const draftBroadcasts = broadcasts.filter((b) => b.status === "draft");

  return (
    <div className="broadcast-container">
      {notification.show && (
        <div className={`broadcast-notification ${notification.type}`}>
          {notification.message}
        </div>
      )}

      <div className="broadcast-header">
        <h1>Image Broadcasting</h1>
        <p>Each image sent as separate broadcast • Drag to reorder</p>
      </div>

      <div className="tabs">
        <button
          className={activeTab === "compose" ? "active" : ""}
          onClick={() => setActiveTab("compose")}
        >
          Compose
        </button>
        <button
          className={activeTab === "drafts" ? "active" : ""}
          onClick={() => setActiveTab("drafts")}
        >
          Drafts ({draftBroadcasts.length})
        </button>
        <button
          className={activeTab === "sent" ? "active" : ""}
          onClick={() => setActiveTab("sent")}
        >
          Sent ({sentBroadcasts.length})
        </button>
      </div>

      {/* ===================== COMPOSE TAB ===================== */}
      {activeTab === "compose" && (
        <div className="card">
          <h2>{editingDraftId ? "Edit Draft" : "Send Images"}</h2>

          <div className="form-grid">
            <div className="form-group">
              <label>Images * (Drag to reorder)</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                style={{ marginBottom: "12px" }}
              />

              {imagePreviews.length > 0 && (
                <div className="image-previews-grid">
                  {imagePreviews.map((preview, index) => (
                    <div
                      key={index}
                      className="preview-item"
                      draggable
                      onDragStart={(e) =>
                        e.dataTransfer.setData("text/plain", index)
                      }
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const from = parseInt(
                          e.dataTransfer.getData("text/plain"),
                        );
                        moveImage(from, index);
                      }}
                    >
                      <div className="preview-box">
                        <img src={preview} alt={`Preview ${index + 1}`} />
                      </div>
                      <button
                        onClick={() => removeImage(index)}
                        className="remove-btn"
                        title="Remove"
                      >
                        <X size={16} />
                      </button>
                      <div className="order-number">{index + 1}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {isUploading && (
              <div className="progress-container">
                <p>
                  Uploading {currentUploadingIndex} of {imageFiles.length}...
                </p>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p>{uploadProgress}%</p>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label>Validity</label>
                <select
                  value={validity}
                  onChange={(e) => setValidity(e.target.value)}
                >
                  <option value="1 day">1 day</option>
                  <option value="2 days">2 days</option>
                  <option value="1 week">1 week</option>
                  <option value="2 weeks">2 weeks</option>
                  <option value="1 month">1 month</option>
                </select>
              </div>

              <div className="form-group full-width">
                <label>Recipients * ({selectedUsers.length} selected)</label>
                <div className="user-selector-card">
                  <div className="selector-header">
                    <input
                      type="text"
                      placeholder="Search companies..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <div className="selector-actions">
                      <button
                        onClick={() =>
                          setSelectedUsers(users.map((u) => u.userId))
                        }
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => setSelectedUsers([])}
                        className="clear"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="user-list">
                    {filteredUsers.map((user) => (
                      <label key={user.userId} className="user-item">
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.userId)}
                          onChange={() => handleUserSelect(user.userId)}
                        />
                        <div>
                          <div className="company">{user.company_name}</div>
                          <div className="id">
                            ID: {user.userId.slice(0, 10)}...
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="actions">
            <button
              onClick={() => saveBroadcast("sent")}
              className="send"
              disabled={
                loading ||
                isUploading ||
                imageFiles.length + existingImageUrls.length === 0
              }
            >
              {isUploading
                ? "Sending..."
                : `Send ${imageFiles.length + existingImageUrls.length} Image(s)`}
            </button>

            <button
              onClick={() => saveBroadcast("draft")}
              disabled={loading || isUploading}
            >
              Save as Draft
            </button>

            {editingDraftId && (
              <button
                onClick={() => {
                  setEditingDraftId(null);
                  setImageFiles([]);
                  setImagePreviews([]);
                  setExistingImageUrls([]);
                  setSelectedUsers([]);
                  setValidity("2 days");
                }}
              >
                Cancel Edit
              </button>
            )}
          </div>
        </div>
      )}

      {/* ===================== DRAFTS & SENT TABS ===================== */}
      {(activeTab === "drafts" || activeTab === "sent") && (
        <div className="broadcasts-grid">
          {(activeTab === "drafts" ? draftBroadcasts : sentBroadcasts).map(
            (b) => (
              <div key={b.id} className="broadcast-card">
                <div className="card-header">
                  <h3>Image Broadcast</h3>
                  <div className="actions">
                    {activeTab === "drafts" && (
                      <>
                        <button onClick={() => handleEditDraft(b)} title="Edit">
                          <Pencil size={18} />
                        </button>
                        <button
                          onClick={() => saveBroadcast("sent", b)}
                          title="Send Now"
                          style={{ color: "#27ae60" }}
                        >
                          <Send size={18} />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() =>
                        handleDelete(
                          b.id,
                          activeTab === "drafts" ? "draft" : "broadcast",
                        )
                      }
                      title="Delete"
                      className="delete-btn"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>

                <span className={`badge ${b.status}`}>
                  {b.status === "draft" ? "Draft" : "Sent"}
                </span>

                {b.imageUrl && (
                  <div className="broadcast-image-box">
                    <img src={b.imageUrl} alt="Broadcast" />
                  </div>
                )}

                <p className="date">
                  {b.status === "draft" ? "Saved" : "Sent"}:{" "}
                  {new Date(b.timestamp).toLocaleString()}
                </p>

                <div className="footer">
                  <span>Recipients: {b.recipients?.length || 0}</span>
                  <span>Validity: {b.validity}</span>
                </div>
              </div>
            ),
          )}

          {activeTab === "drafts" && draftBroadcasts.length === 0 && (
            <div className="empty">No drafts yet</div>
          )}
          {activeTab === "sent" && sentBroadcasts.length === 0 && (
            <div className="empty">No sent broadcasts yet</div>
          )}
        </div>
      )}
    </div>
  );
};

export default BroadcastingPage;
